
import { useState, useEffect, useCallback } from 'react';
import { usersAPI, swapsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import UserCard from '../components/UserCard';
import SwapRequestModal from '../components/SwapRequestModal';
import './Home.css';

const Home = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [swapMap, setSwapMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchSwapMap = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await swapsAPI.getAll();
      const map = {};
      data.forEach(swap => {
        if (!['pending', 'accepted'].includes(swap.status)) return;
        const otherId =
          swap.sender?._id === user._id ? swap.receiver?._id : swap.sender?._id;
        if (!otherId) return;
        const existing = map[otherId];
        if (!existing) {
          map[otherId] = swap;
          return;
        }
        const priority = { accepted: 3, pending: 2 };
        if ((priority[swap.status] || 0) > (priority[existing.status] || 0)) {
          map[otherId] = swap;
        }
      });
      setSwapMap(map);
    } catch (err) {
      console.error(err);
    }
  }, [user]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (skillFilter) params.skill = skillFilter;
      const { data } = await usersAPI.getAll(params);
      setUsers(data.users);
      setTotalPages(data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, skillFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchSwapMap(); }, [fetchSwapMap]);
  useEffect(() => { setPage(1); }, [debouncedSearch, skillFilter]);

  const handleSwapSuccess = () => {
    setSuccessMsg('Swap request sent! Waiting for their response.');
    setTimeout(() => setSuccessMsg(''), 4000);
    fetchSwapMap();
  };

  const popularSkills = ['Python', 'JavaScript', 'Guitar', 'Spanish', 'Design', 'Yoga', 'Photography', 'Chess'];

  return (
    <div className="home-page">
      <div className="home-hero">
        <div className="page-container">
          <div className="hero-content">
            <h1>Find Your Perfect<br />Skill Exchange</h1>
            <p>Connect with {users.length > 0 ? 'learners and teachers' : 'people'} ready to swap knowledge</p>
            <div className="search-bar">
              <span className="search-icon">⌕</span>
              <input
                type="text"
                placeholder="Search by name, skill, or topic…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ border: 'none', boxShadow: 'none', background: 'transparent', fontSize: 15 }}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-container">
        <div className="home-filters">
          <div className="filter-scroll">
            <button
              className={`filter-chip ${!skillFilter ? 'active' : ''}`}
              onClick={() => setSkillFilter('')}
            >All Skills</button>
            {popularSkills.map(skill => (
              <button
                key={skill}
                className={`filter-chip ${skillFilter === skill ? 'active' : ''}`}
                onClick={() => setSkillFilter(skillFilter === skill ? '' : skill)}
              >
                {skill}
              </button>
            ))}
          </div>
          <span className="result-count">
            {loading ? '...' : `${users.length} people`}
          </span>
        </div>

        {successMsg && (
          <div className="success-banner">
            ✓ {successMsg}
          </div>
        )}

        {loading ? (
          <div className="home-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card-skeleton" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3>No users found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="users-grid">
            {users.map(u => (
              <UserCard
                key={u._id}
                user={u}
                swap={swapMap[u._id] || null}
                currentUserId={user?._id}
                onRequestSwap={() => setSelectedUser(u)}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >← Prev</button>
            <span className="page-info">{page} / {totalPages}</span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >Next →</button>
          </div>
        )}
      </div>

      {selectedUser && (
        <SwapRequestModal
          targetUser={selectedUser}
          currentUser={user}
          onClose={() => setSelectedUser(null)}
          onSuccess={handleSwapSuccess}
        />
      )}
    </div>
  );
};

export default Home;